import discordService from './discordService';
import adminService from './adminService';
import { functionsBase } from '../config/functionsBase';

const FUNCTIONS_BASE = functionsBase('/.netlify/functions');

async function playerFetch(path, options = {}) {
  const token = discordService.getPlayerAccessToken();
  if (!token) {
    const err = new Error(
      'Sessão Discord expirada ou incompleta. Conecte novamente para usar o suporte.'
    );
    err.code = 'NO_TOKEN';
    throw err;
  }

  const res = await fetch(`${FUNCTIONS_BASE}/${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Erro ${res.status}`);
    err.status = res.status;
    err.data = data;
    err.code =
      res.status === 401
        ? 'UNAUTHORIZED'
        : res.status === 429
          ? 'RATE_LIMIT'
          : 'API';
    throw err;
  }
  return data;
}

export const SUPPORT_TOPICS = [
  {
    id: 'payment',
    label: 'Pagamento (cartão / PIX)',
    short: 'Pagamento',
    hint: 'Cartão, PIX ou cobrança',
  },
  {
    id: 'subscription',
    label: 'Assinatura / renovação',
    short: 'Assinatura',
    hint: 'Renovação, cancelamento',
  },
  {
    id: 'access',
    label: 'Acesso, cargo ou rank',
    short: 'Acesso',
    hint: 'Cargo Discord ou rank',
  },
  {
    id: 'other',
    label: 'Outro assunto VIP',
    short: 'Outro',
    hint: 'Demais dúvidas da loja',
  },
];

export async function resumeSupportChat() {
  return playerFetch('support-chat-start', {
    method: 'POST',
    body: JSON.stringify({ resume: true }),
  });
}

export async function startSupportChat({ topic, message }) {
  return playerFetch('support-chat-start', {
    method: 'POST',
    body: JSON.stringify({ topic, message }),
  });
}

export async function syncSupportChat(chatId, { sinceSeq = 0, role = 'user' } = {}) {
  const params = new URLSearchParams({
    chatId: String(chatId),
    sinceSeq: String(sinceSeq),
  });
  if (role === 'staff') params.set('role', 'staff');

  if (role === 'staff') {
    return adminServiceFetch(`support-chat-sync?${params}`);
  }
  return playerFetch(`support-chat-sync?${params}`);
}

export async function sendSupportMessage(
  chatId,
  body,
  { role = 'user', attachment = null } = {}
) {
  const payload = { chatId, body, role };
  if (attachment) payload.attachment = attachment;
  if (role === 'staff') {
    return adminServiceFetch('support-chat-send', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }
  return playerFetch('support-chat-send', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function rateSupportChat(chatId, { rating, comment = '' }) {
  return playerFetch('support-chat-rate', {
    method: 'POST',
    body: JSON.stringify({ chatId, rating, comment }),
  });
}

export async function fetchSupportHours() {
  return playerFetch('support-chat-settings');
}

export async function fetchSupportSettings() {
  return adminSupportFetch('support-chat-settings?role=staff');
}

export async function updateSupportSettings(patch) {
  return adminSupportFetch('support-chat-settings?role=staff', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

export async function fetchSupportStats({ days = 14 } = {}) {
  return adminSupportFetch(`support-chat-stats?days=${encodeURIComponent(days)}`);
}

export async function pingSupportTyping(chatId, { role = 'user' } = {}) {
  const payload = { chatId, typing: true, role };
  if (role === 'staff') {
    return adminServiceFetch('support-chat-send', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }
  return playerFetch('support-chat-send', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

async function adminServiceFetch(path, options = {}) {
  // Reusa adminFetch interno via métodos públicos novos
  return adminSupportFetch(path, options);
}

async function adminSupportFetch(path, options = {}) {
  const token = adminService.getAccessToken();
  if (!token) {
    const err = new Error('Sessão admin expirada.');
    err.code = 'NO_TOKEN';
    throw err;
  }
  const res = await fetch(`${FUNCTIONS_BASE}/${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Erro ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function listSupportChats(params = {}) {
  const qs = new URLSearchParams();
  if (params.filter) qs.set('filter', params.filter);
  if (params.topic) qs.set('topic', params.topic);
  if (params.q) qs.set('q', params.q);
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.skip) qs.set('skip', String(params.skip));
  return adminSupportFetch(`support-chat-list?${qs}`);
}

export async function supportChatAction(payload) {
  return adminSupportFetch('support-chat-action', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** Exclui chats fechados (chatIds) ou todos fechados (allClosed). */
export async function deleteSupportChats(payload) {
  return adminSupportFetch('support-chat-delete', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function supportChatContext(chatId, { refresh = false } = {}) {
  const qs = new URLSearchParams({ chatId: String(chatId) });
  if (refresh) qs.set('refresh', '1');
  return adminSupportFetch(`support-chat-context?${qs}`);
}

export async function supportChatVipAction(payload) {
  return adminSupportFetch('support-chat-vip-action', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

const supportChatService = {
  SUPPORT_TOPICS,
  resumeSupportChat,
  startSupportChat,
  syncSupportChat,
  sendSupportMessage,
  pingSupportTyping,
  rateSupportChat,
  fetchSupportHours,
  fetchSupportSettings,
  updateSupportSettings,
  fetchSupportStats,
  listSupportChats,
  supportChatAction,
  deleteSupportChats,
  supportChatContext,
  supportChatVipAction,
};

export default supportChatService;
